import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { chunk } from '@/pages/PatientListPage/services/pacQueueHydration';
import { fetchBotRead } from '@/services/botCalls';
import { buildLedgerRows, LedgerRow } from '../ledger/ledgerRows';
import { REFUND_WORKLIST_SINCE, RefundRequest, RefundWorklists } from './refundRequests';

/** The Refunds view's two worklists, through the bot (BAR-3033), each on its patient. */

export interface RefundRequestRow extends RefundRequest {
  patientDisplayName: string;
}

export interface RefundWorklistsState {
  requests: RefundRequestRow[];
  refunds: LedgerRow[];
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

export function useRefundWorklists(): RefundWorklistsState {
  const medplum = useMedplum();
  const [state, setState] = useState<Pick<RefundWorklistsState, 'requests' | 'refunds' | 'error'>>({
    requests: [],
    refunds: [],
    error: undefined,
  });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function load(): Promise<void> {
      const { worklists } = await fetchBotRead<{ worklists: RefundWorklists }>(medplum, 'create-treatment-payment', {
        operation: 'refundWorklists',
        since: REFUND_WORKLIST_SINCE,
      });
      const ids = [
        ...new Set(
          [...worklists.requests, ...worklists.unlinkedRefunds]
            .map((item) => item.medplumPatientId)
            .filter((id): id is string => Boolean(id))
        ),
      ];
      const patients = (
        await Promise.all(
          chunk(ids).map((idChunk) => medplum.searchResources('Patient', { _id: idChunk.join(','), _count: '1000' }))
        )
      ).flat();
      const patientById = new Map<string, Patient>(patients.map((patient) => [patient.id as string, patient]));

      if (!cancelled) {
        setState({
          requests: worklists.requests
            .map((request) => {
              const patient = request.medplumPatientId ? patientById.get(request.medplumPatientId) : undefined;
              const name = patient ? formatGivenFamilyName(patient.name?.[0]) : '';
              return { ...request, patientDisplayName: name || request.patientName || '' };
            })
            .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()),
          refunds: buildLedgerRows(worklists.unlinkedRefunds, patientById),
          error: undefined,
        });
      }
    }

    load()
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            requests: [],
            refunds: [],
            error: error instanceof Error ? error.message : 'Could not load refunds',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, reloadKey]);

  return { ...state, loading, reload };
}
